import { Currency } from "@redotech/money/currencies";
import { moneyFormat } from "@redotech/money/util";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  rcoCartValueSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import {
  CartValueKind,
  SegmentNumberOperator,
} from "@redotech/redo-model/checkouts/checkout-tree";
import { RedoCurrencyInput } from "@redotech/redo-web/arbiter-components/input/redo-currency-input";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { safeParseEnum } from "@redotech/util/enum";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";

export const CartValueInspectorContent = memo(
  function CartValueInspectorContent({ step }: { step: RcoSegmentStep }) {
    const team = useRequiredContext(TeamContext);
    const shopCurrency =
      safeParseEnum(team._shopify?.currency, Currency) ?? Currency.USD;

    const segment = useMemo(
      () => rcoCartValueSegmentSchema.parse(step.segment),
      [step],
    );

    return (
      <Flex align="flex-start" dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          Condition
        </Text>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {cartValueKindToText[segment.valueKind]} cart value is{" "}
          {segmentNumberOperatorToText[segment.operator]}{" "}
          {moneyFormat({
            amount: segment.value.amount,
            currency: shopCurrency,
          })}
        </Text>
      </Flex>
    );
  },
);

export const EditableCartValueInspectorContent = memo(
  function EditableCartValueInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const team = useRequiredContext(TeamContext);
    const shopCurrency =
      safeParseEnum(team._shopify?.currency, Currency) ?? Currency.USD;

    const segment = useMemo(
      () => rcoCartValueSegmentSchema.parse(step.segment),
      [step],
    );

    const segmentValue = useMemo(() => {
      return { amount: segment.value.amount, currency: shopCurrency };
    }, [segment.value, shopCurrency]);

    const handleOperatorChange = useHandler(
      (operator: SegmentNumberOperator) => {
        const updatedSegment = { ...segment, operator };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handleValueChange = useHandler((value?: { amount: number }) => {
      const valueToSet = value ?? { amount: 0 };
      const updatedSegment = { ...segment, value: valueToSet };
      setStep({ ...step, segment: updatedSegment });
    });

    const handleValueKindChange = useHandler((valueKind: CartValueKind) => {
      const updatedSegment = { ...segment, valueKind };
      setStep({ ...step, segment: updatedSegment });
    });

    const selectedOperator = useMemo(() => {
      return operatorOptions.find(
        (option) => option.value === segment.operator,
      );
    }, [segment.operator]);

    const selectedValueKind = useMemo(() => {
      return valueKindOptions.find(
        (option) => option.value === segment.valueKind,
      );
    }, [segment.valueKind]);

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Condition
          </Text>
        </Flex>
        <Flex dir="column" gap="md" w="full">
          <Flex align="center" dir="row" gap="md" wrap="wrap">
            <RedoSingleSelectDropdownInput<CartValueKind>
              fitToAnchor={false}
              flexGrow={false}
              options={valueKindOptions}
              optionSelected={({ item }) => handleValueKindChange(item.value)}
              placeholder="Select value type"
              selectedItem={selectedValueKind}
              size="xs"
            />
            <Text fontSize="xs" fontWeight="medium">
              cart value is
            </Text>
            <RedoSingleSelectDropdownInput<SegmentNumberOperator>
              fitToAnchor={false}
              flexGrow={false}
              options={operatorOptions}
              optionSelected={({ item }) => handleOperatorChange(item.value)}
              placeholder="Select comparison"
              selectedItem={selectedOperator}
              size="xs"
            />
          </Flex>
          <Flex align="center" dir="row" gap="sm">
            <RedoCurrencyInput
              placeholder={0}
              setValue={handleValueChange}
              size="sm"
              value={segmentValue}
            />
          </Flex>
        </Flex>
      </>
    );
  },
);

const operatorOptions: TextRedoListItem<SegmentNumberOperator>[] = [
  {
    type: "text",
    id: SegmentNumberOperator.LESS_THAN,
    value: SegmentNumberOperator.LESS_THAN,
    text: "less than",
  },
  {
    type: "text",
    id: SegmentNumberOperator.LESS_THAN_OR_EQUAL,
    value: SegmentNumberOperator.LESS_THAN_OR_EQUAL,
    text: "less than or equal to",
  },
  {
    type: "text",
    id: SegmentNumberOperator.EQUAL,
    value: SegmentNumberOperator.EQUAL,
    text: "equal to",
  },
  {
    type: "text",
    id: SegmentNumberOperator.GREATER_THAN,
    value: SegmentNumberOperator.GREATER_THAN,
    text: "greater than",
  },
  {
    type: "text",
    id: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
    value: SegmentNumberOperator.GREATER_THAN_OR_EQUAL,
    text: "greater than or equal to",
  },
];

const valueKindOptions: TextRedoListItem<CartValueKind>[] = [
  {
    type: "text",
    id: CartValueKind.TOTAL,
    value: CartValueKind.TOTAL,
    text: "Total",
  },
  {
    type: "text",
    id: CartValueKind.DISCOUNTED,
    value: CartValueKind.DISCOUNTED,
    text: "Discounted",
  },
];

const segmentNumberOperatorToText: Record<SegmentNumberOperator, string> = {
  [SegmentNumberOperator.LESS_THAN]: "less than",
  [SegmentNumberOperator.LESS_THAN_OR_EQUAL]: "less than or equal to",
  [SegmentNumberOperator.EQUAL]: "equal to",
  [SegmentNumberOperator.GREATER_THAN]: "greater than",
  [SegmentNumberOperator.GREATER_THAN_OR_EQUAL]: "greater than or equal to",
};

const cartValueKindToText: Record<CartValueKind, string> = {
  [CartValueKind.TOTAL]: "Total",
  [CartValueKind.DISCOUNTED]: "Discounted",
};
